


























import Vue from 'vue';
import Component from 'vue-class-component';
import { Prop } from 'vue-property-decorator';
import mapboxgl, { MapMouseEvent, CameraOptions } from 'mapbox-gl';
import Bro from 'brototype';

import '../globals';
import { Route } from '@/models/route';
import { Poi, PoiState } from '@/models/poi';
import { RouteFilterSet } from '@/models/filter';
import RouteCardSlider from '@/components/route-card-slider.vue';
import MapRouteHelper from '@/services/map-route-helper';
import MapPoiHelper from '@/services/map-poi-helper';
import MapPositionHelper from '@/services/map-position-helper';
import FilterHelper from '@/services/filter-helper';
import { delay } from '../utils';

@Component({
    components: {
        RouteCardSlider,
    },
})
export default class MapRoutePage extends Vue {

    public routes = new Array<Route>();
    public selectedRoute: Route;

    public map?: mapboxgl.Map;
    public slider?: RouteCardSlider;

    public poisGeoJson: GeoJSON.FeatureCollection = {
        type: 'FeatureCollection',
        features: []
    };

    public routeBoxesGeoJson: GeoJSON.FeatureCollection = {
        type: 'FeatureCollection',
        features: [],
    };

    private isMapLoaded: boolean;
    private filterSet: RouteFilterSet;
    private isFiltered: boolean = false;

    private static mapInstance: number = 0;
    private mapId: string;

    public created() {
        // init map id
        MapRoutePage.mapInstance += 1;
        this.mapId = 'route_map_id_' + MapRoutePage.mapInstance;

        this.filterSet = FilterHelper.createRouteFilterSet();
        this.updateFilterIcon();
    }

    public mounted() {
        this.slider = this.$refs.routecardslider as RouteCardSlider;

        this.$store.$on('storeUpdated', this.onStoreUpdated);
        this.$on('routeFilterApplied', this.onRouteFilterApplied);
    }

    public beforeDestroy() {
        this.$store.$off('storeUpdated', this.onStoreUpdated);
        this.$off('routeFilterApplied', this.onRouteFilterApplied);

        if (this.map) {
            // removes event binding implicit
            this.map.remove();
        }
    }

    public async onAppearing() {
        if (this.map && this.isMapLoaded) {
            await this.updateFilteredMapData();
        }
        else {
            this.loadMap();
            //updateFilteredMapData() is called in mapLoaded event the first time
        }
    }

    public onDisappearing() {
        MapPositionHelper.saveMapPosition(this.map, this.$store);
    }

    public async onStoreUpdated() {
        this.routes = FilterHelper.getFilteredRoutes(this.$store.routes, this.filterSet);

        if (this.map && this.isMapLoaded) {
            await this.updateFilteredMapData();
        }
    }

    public loadMap() {
        const mapStyle = process.env.VUE_APP_MAPBOX_STYLE;
        const mapPos: CameraOptions = (this.$store.mapPosition) ? this.$store.mapPosition : {
            center: [9.556633, 47.111808],
            zoom: 10,
            bearing: 0
        };

        this.map = new mapboxgl.Map({
            container: this.mapId,
            style: mapStyle,
            center: mapPos.center,
            zoom: mapPos.zoom,
            bearing: mapPos.bearing,
            minZoom: 9,
            maxZoom: 18,
            maxBounds: [
                [9.339637, 46.908183], // South West
                [9.776214, 47.391206] // North East
            ],
        });
        this.map.on('load', this.mapLoaded);
        this.map.on('click', this.onMapClicked);
    }

    public async mapLoaded() {
        // wait until store is ready :)
        await this.$store.waitUntilReady();

        // load all routes
        this.routes = Array.from(this.$store.routes);
        MapRouteHelper.addAllRoutes(this.map, this.routes);

        // load all route hit boxes
        this.routeBoxesGeoJson.features = MapRouteHelper.createBoundingBoxesGeoJSON(this.routes);
        this.map.addSource('routeBoxes', { type: 'geojson', data: this.routeBoxesGeoJson });
        MapRouteHelper.addRouteBoxLayer(this.map);

        // load empty pois
        this.map.addSource('pois', { type: 'geojson', data: this.poisGeoJson });
        MapPoiHelper.addRouteOverviewPois(this.map, 'pois');

        // update date for first show off
        await this.updateFilteredMapData();

        this.isMapLoaded = true;
    }

    private async updateFilteredMapData() {
        if (!this.routes || this.routes.length <= 0) {
            return;
        }

        // Needed to fix layout issues on devices with safe areas
        //this.map.resize();

        const activeRouteIds = new Array<Number>();

        // show and hide route layers
        for (const route of this.$store.routes) {
            if (this.routes.indexOf(route) >= 0) {
                MapRouteHelper.showRoute(this.map, route);
                activeRouteIds.push(route.id);
            }
            else {
                MapRouteHelper.hideRoute(this.map, route);
            }
        }

        // build list of pois for active routes
        const activePois = new Array<Poi>();
        for (const poi of this.$store.pois) {
            if (activeRouteIds.includes(poi.route)) {
                activePois.push(poi);
            }
        }
        this.poisGeoJson.features = MapPoiHelper.createPoisGeoJSON(activePois);

        // reselect route anyway
        if (!this.selectedRoute) {
            if (this.routes && this.routes.length > 0) {
                // select first if there is one
                await this.selectRoute(this.routes[0], true);
            }
            else {
                // non is selected
                await this.selectRoute(null, false);
            }
        }
        else {
            if (this.routes.indexOf(this.selectedRoute) < 0) {
                // select first if the rest
                await this.selectRoute(this.routes[0], true);
            }
            else {
                // select same for saftey reasons
                await this.selectRoute(this.selectedRoute, false);
            }
        }

        MapPositionHelper.restoreMapPosition(this.map, this.$store);
    }

    private async selectRoute(route: Route, doFocus: boolean = false, updateSlider: boolean = true) {
        // unselect old
        if (this.selectedRoute) {
            MapRouteHelper.unselectedRoute(this.map, this.selectedRoute);
            this.selectedRoute = null;
        }

        // select new selected
        if (route) {
            MapRouteHelper.selectedRoute(this.map, route);
            this.selectedRoute = route;

            // update selected pois
            const poisOfSelectedRoute = this.$store.pois.filter(poi => poi.route === this.selectedRoute.id);
            MapPoiHelper.selectPois(this.poisGeoJson.features, poisOfSelectedRoute);
            const poisSource = this.map.getSource('pois') as mapboxgl.GeoJSONSource;
            poisSource.setData(this.poisGeoJson);

            // update slider
            if (updateSlider && this.routes && this.routes.length > 0) {
                const selectedSlideIndex = this.routes.indexOf(this.selectedRoute);
                if (selectedSlideIndex >= 0 && this.slider.activeIndex != selectedSlideIndex) {
                    this.slider.jumpToIndex(selectedSlideIndex);
                }
            }
            else if (doFocus) {

                // doesn't work without a delay (especially when coming from other page / example: filters)
                await delay(100);
                MapRouteHelper.focusRoute(this.map, this.selectedRoute);
            }
        }
    }

    private onRouteFilterApplied(filterSet: RouteFilterSet, filteredRoutes: Route[]) {
        this.filterSet = filterSet;
        this.routes = filteredRoutes;
        this.updateFilterIcon();

        // data will be applied in onappearing :)
    }

    private onMapClicked(event: MapMouseEvent) {
        const routeId = MapRouteHelper.findRouteIdFor(this.map, event);
        const routeIndex = this.routes.findIndex(p => p.id === routeId);

        if (routeIndex >= 0) {
            this.slider.jumpToIndex(routeIndex);
        }
    }

    private async routeCardChanged() {
        const route = this.routeForSlide(this.slider.activeIndex);
        await this.selectRoute(route, true, false);
    }

    private routeCardTapped() {
        let index = 0;
        if (this.slider.activeIndex !== undefined) {
            index = this.slider.activeIndex;
        }

        const route = this.routeForSlide(index);
        this.$f7router.navigate('/routes/' + route.id);
    }

    private routeForSlide(slideIndex: number) {
        if (slideIndex < 0 || slideIndex > this.routes.length - 1) {
            console.error('Route index ' + slideIndex + ' not found in store');
            return undefined;
        }

        return this.routes[slideIndex];
    }

    private updateFilterIcon() {
        this.isFiltered = FilterHelper.isRouteFilterSetModified(this.filterSet);
    }

    private onRouteFilterClicked() {
        this.$f7router.navigate('/routes/filter/', {
            props: {
                routeSource: this.$store.routes,
                filterSet: this.filterSet,
                eventTarget: this
            }
        });
    }
}
